<template>
    <div class="productlist-items">
        <div v-if="topProducts.length" class="row justify-content-center product-list">
            <div v-for="(item, i) in topProducts" :key="item.id" class="col-12 col-lg-6">
                <ObjectsProduct
                    :highlight="true"
                    :show-description="showProductsDescription"
                    :item="item"
                    :list-position="i"
                    @product-redirected-to-url="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Click)"
                    @product-added-to-basket="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Conversion)"
                />
            </div>
        </div>
        <div class="product-list" data-cy="product-list">
            <div v-for="(item, i) in items" :key="item.id" class="product-list__item">
                <template v-if="item.type === 'catalog_banner'">
                    <a ref="catalogBanners" class="product-list__banner" :href="item.url" :data-id="item.id" :data-title="item.title">
                        <img :src="convertToBanner(item).cdnImage" alt="Banner" loading="lazy" />
                    </a>
                    <WarningMessageInBanner
                        v-if="convertToBanner(item).warningMessage?.length"
                        :message="convertToBanner(item).warningMessage"
                        :modal-id="`${item.id}–warningMessage`"
                    />
                </template>
                <ObjectsProduct
                    v-else
                    :show-description="showProductsDescription"
                    :item="item"
                    :lazy-image="i >= 3 && lazyImage"
                    :list-position="topProducts.length + i"
                    :list-title="listTitle"
                    :list-type="listType"
                    :item-list-name="itemListName"
                    @product-redirected-to-url="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Click)"
                    @product-added-to-basket="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Conversion)"
                />
            </div>
        </div>

        <slot />
    </div>
</template>

<script lang="ts">
import { list } from 'postcss'
import WarningMessageInBanner from '~/components/elements/WarningMessageInBanner.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import Product from '~/models/Product/Product'
import Banner from '~/models/components/Banner'
import { LuigisBoxClickEventTypeEnum } from '~/models/LuigisBox/LuigisBoxClickEventTypeEnum'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { useMatomoViewportTrack, useMatomoClickTrack } from '~/composables/useMatomo'

export default {
    components: {
        WarningMessageInBanner,
    },
    props: {
        items: {
            type: Array as PropType<Product[]>,
            default: null,
        },
        topProducts: {
            type: Array as PropType<Product[]>,
            default: null,
        },
        lazyImage: {
            type: Boolean,
            default: true,
        },

        listTitle: {
            type: String,
            default: '',
        },

        listType: {
            type: String,
            default: '',
        },

        showProductsDescription: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        list() {
            return list
        },

        itemListName() {
            if (this.listType === 'search') {
                return GtmItemListNameEnum.SearchResults
            }

            return GtmItemListNameEnum.Catalogue
        },

        LuigisBoxClickEventTypeEnum() {
            return LuigisBoxClickEventTypeEnum
        },
    },

    mounted() {
        const elements = this.$refs.catalogBanners
        if (elements) {
            elements.forEach((item: HTMLElement) => {
                useMatomoViewportTrack(item, {
                    category: 'Catalog Banners',
                    action: 'view',
                    name: item.dataset.title,
                    value: +(item.dataset.id ?? 0),
                })

                useMatomoClickTrack(item, {
                    category: 'Catalog Banners',
                    action: 'click',
                    name: item.dataset.title,
                    value: +(item.dataset.id ?? 0),
                })
            })
        }
    },

    methods: {
        convertToBanner(product: Product): Banner {
            return product as unknown as Banner
        },

        sendLuigisboxEvent(productUrl: string, type: LuigisBoxClickEventTypeEnum) {
            if (this.listType === 'search') {
                LuigisBoxEventApi.sendClickEvent(type, productUrl)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.productlist-items {
    margin-bottom: 2em;
    container-type: inline-size;

    @media (max-width: 480px) {
        margin-left: -15px;
        margin-right: -15px;
    }
}

$gap: 8px;
.product-list {
    gap: $gap;
    padding: 0;
    display: flex;
    flex-wrap: wrap;

    //@include screen-md {
    //    padding: 0 8px;
    //}

    &__banner {
        display: block;
        border-bottom: 1px solid $color-grey-dark;
        width: 100%;
        margin-top: 25px;

        @include screen-sm {
            border-radius: 10px;
            border-bottom: none;
        }

        &:hover {
            box-shadow: 0 1px 6px rgb(148 170 179 / 30%);
            transition: 150ms ease-in-out;
        }

        img {
            width: 100%;
        }
    }

    &__item {
        position: relative;
        padding: 0 0 6px;
        flex: 0 0 calc(100% / 2 - ($gap / 2 * 1));

        @container (min-width: 800px) {
            flex: 0 0 calc(100% / 3 - ($gap / 3 * 2));
        }

        @container (min-width: 850px) {
            flex: 0 0 calc(100% / 4 - ($gap / 4 * 3));
        }

        //@include screen-sm {
        //    flex: 0 0 calc(100% / 3 - ($gap / 3 * 2));
        //}
        //
        //@include screen-md {
        //    flex: 0 0 calc(100% / 2 - ($gap / 2 * 1));
        //}
        //
        //@include screen-lg {
        //    flex: 0 0 calc(100% / 3 - ($gap / 3 * 2));
        //}
        //
        //@include screen-xl {
        //    flex: 0 0 calc(100% / 4 - ($gap / 4 * 3));
        //}

        @media (max-width: 480px) {
            padding: 0 !important;
        }
    }

    @media (max-width: 480px) {
        &__banner {
            display: inline-block;
            border-bottom: 1px solid $color-grey-dark;
            height: 100%;
        }
    }

    .product {
        height: 100%;

        @media (max-width: 480px) {
            border-bottom: 1px solid $color-grey-dark;
            border-right: 1px solid $color-grey-dark;
            margin: 0;
            border-radius: 0;
        }
        &.product--row {
            @media (max-width: 480px) {
                border-right: 0;
            }
        }
    }

    .product-list__item:nth-child(even) .product {
        @media (max-width: 480px) {
            border-right: 0;
        }
    }
}
</style>
